import * as React from 'react';

type TruncationAxis = 'width' | 'height';

/**
 * Хук для определения обрезания текста (ellipsis) с учётом ResizeObserver.
 * Пересчёт выполняется при монтировании и при изменении размеров элемента.
 *
 * @param axis - по какой оси проверять: 'width' (одна строка) или 'height' (line-clamp)
 * @returns [ref, isTruncated] — ref передавать в ref элемента (callback ref для корректной работы при условном рендере)
 */
export function useIsTextTruncated(axis: TruncationAxis = 'width'): [React.RefCallback<HTMLDivElement>, boolean] {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const [elementVersion, setElementVersion] = React.useState(0);

  const setRef = React.useCallback((node: HTMLDivElement | null) => {
    ref.current = node;

    setElementVersion((v) => v + 1);
  }, []);

  React.useEffect(() => {
    const el = ref.current;

    if (!el) {
      setIsTruncated(false);

      return;
    }

    const check = () => {
      if (!ref.current) {
        return;
      }

      const { scrollWidth, clientWidth, scrollHeight, clientHeight } = ref.current;

      if (axis === 'width') {
        setIsTruncated(scrollWidth > clientWidth);
      } else {
        setIsTruncated(scrollHeight - 1 > clientHeight);
      }
    };

    const resizeObserver = new ResizeObserver(check);

    resizeObserver.observe(el);
    check();

    return () => {
      resizeObserver.disconnect();
    };
  }, [axis, elementVersion]);

  return [setRef, isTruncated];
}
